import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import NotificationBanner from "../../components/NotificationBanner";
import { useCompanyDefaults } from "../../utils/companyDefaults";
import s from "./MyCalendar.module.css";

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}

function resolveEmployeeCode(u) {
  if (!u) return "";
  if (u.employeeCode) return u.employeeCode;
  if (u.linkedEntity && u.linkedEntity.code) return u.linkedEntity.code;
  return "";
}

function parseLeaveCredits(raw) {
  if (!raw) return [];
  try {
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(d)) return [];
    return d.map(o => ({
      name: String(o?.name || ""),
      days: Number(o?.days) || 0,
    })).filter(l => l.name);
  } catch { return []; }
}

function mergeWithDefaults(empLeaves, defaultsRaw) {
  const defaults = parseLeaveCredits(defaultsRaw);
  if (defaults.length === 0) return empLeaves;
  const names = new Set(empLeaves.map(l => (l.name || "").trim().toLowerCase()));
  const missing = defaults.filter(d => !names.has((d.name || "").trim().toLowerCase()));
  return missing.length === 0 ? empLeaves : [...empLeaves, ...missing];
}

function fmtDateLong(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function rangeDays(start, end) {
  const a = new Date(start + "T00:00:00");
  const b = new Date((end || start) + "T00:00:00");
  if (isNaN(a) || isNaN(b)) return 0;
  let n = 0;
  const cur = new Date(a);
  while (cur <= b) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) n += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return n;
}

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function isoOf(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const TIME_HOURS   = ["1","2","3","4","5","6","7","8","9","10","11","12"];
const TIME_MINUTES = ["00","15","30","45"];

/** Custom time picker: three styled <select>s for Hour / Minute / AM-PM.
 *  We avoid the native <input type="time"> because its dropdown panel
 *  ignores theming and clashes with the maroon/gold surface. The value
 *  shape is "HH:mm" in 24-hour format so the backend column stays
 *  compatible with the school /calendar's TimePicker. Empty string
 *  means "no time set" (i.e. full-day leave). */
function TimePicker({ value, onChange, disabled }) {
  let hr12 = "", min = "00", ampm = "AM";
  if (value && /^\d{2}:\d{2}$/.test(value)) {
    const [h, m] = value.split(":").map(Number);
    ampm = h >= 12 ? "PM" : "AM";
    hr12 = String(h % 12 || 12);
    min  = String(m).padStart(2, "0");
  }

  function emit(newHr, newMin, newAmpm) {
    if (!newHr) { onChange(""); return; }
    let h24 = parseInt(newHr, 10);
    if (newAmpm === "PM" && h24 !== 12) h24 += 12;
    if (newAmpm === "AM" && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, "0")}:${newMin}`);
  }

  return (
    <div className={s.timePicker}>
      <select className={s.timeSel} value={hr12} disabled={disabled}
              onChange={e => emit(e.target.value, min, ampm)}
              aria-label="Hour">
        <option value="">--</option>
        {TIME_HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className={s.timeColon} aria-hidden="true">:</span>
      <select className={s.timeSel} value={min} disabled={disabled || !hr12}
              onChange={e => emit(hr12, e.target.value, ampm)}
              aria-label="Minute">
        {TIME_MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <select className={`${s.timeSel} ${s.timeSelAmpm}`} value={ampm}
              disabled={disabled || !hr12}
              onChange={e => emit(hr12, min, e.target.value)}
              aria-label="AM or PM">
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

function todayIso() { return isoOf(new Date()); }

/** Event Details — the focused right-rail view that appears when the
 *  user clicks an event chip. Layout matches the school /payroll-calendar's
 *  Event Details panel for visual consistency, minus the admin-only
 *  Approve / Reject controls (a cashier viewing their own calendar can
 *  only Cancel a still-pending leave; everything else is read-only). */
function EventDetails({ ev, employeeCode, busy, onClose, onCancel }) {
  const own    = ev.createdBy === employeeCode;
  const isLeave = (ev.eventType || "").toLowerCase() === "leave";
  const status = (ev.status || "").toUpperCase();
  const styleKey = eventStyle(ev, employeeCode);
  const hasRange = ev.startDate && ev.endDate && ev.startDate !== ev.endDate;
  const hasTime  = !!ev.startTime;

  return (
    <div className={s.eventDetail}>
      <div className={s.eventHead}>
        <div className={s.eventHeadInner}>
          <div className={s.eventEyebrow}>Event details</div>
          <div className={s.eventTitleRow}>
            <div className={s.eventTitle}>{ev.title}</div>
            <span className={`${s.eventTypePill} ${s["pill_" + styleKey]}`}>
              {eventTypeLabel(ev)}
            </span>
          </div>
        </div>
        <button type="button" className={s.dayCardClose}
                onClick={onClose}
                aria-label="Close event details">×</button>
      </div>

      <div className={s.eventBody}>
        {/* When */}
        <div className={s.eventRow}>
          <span className={s.eventRowIcon} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8"  y1="2" x2="8"  y2="6" />
              <line x1="3"  y1="10" x2="21" y2="10" />
            </svg>
          </span>
          <div className={s.eventRowBody}>
            <div className={s.eventRowLabel}>When</div>
            <div className={s.eventRowValue}>
              {hasRange
                ? `${fmtDateLong(ev.startDate)} → ${fmtDateLong(ev.endDate)}`
                : fmtDateLong(ev.startDate)}
            </div>
            {hasTime && (
              <div className={s.eventRowSub}>
                {ev.startTime}{ev.endTime ? ` → ${ev.endTime}` : ""}
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {ev.notes && (
          <div className={s.eventRow}>
            <span className={s.eventRowIcon} aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </span>
            <div className={s.eventRowBody}>
              <div className={s.eventRowLabel}>Notes</div>
              <div className={s.eventNote}>“{ev.notes}”</div>
            </div>
          </div>
        )}

        {/* Approval — own leaves only (HR events have no status) */}
        {own && isLeave && status && (
          <div className={s.eventRow}>
            <span className={s.eventRowIcon} aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </span>
            <div className={s.eventRowBody}>
              <div className={s.eventRowLabel}>Approval</div>
              <span className={
                status === "APPROVED" ? s.pillOk :
                status === "REJECTED" ? s.pillBad :
                s.pillWait
              }>{status}</span>
              {status === "REJECTED" && ev.decisionNote && (
                <div className={s.eventDecision}>
                  <strong>Reason:</strong> {ev.decisionNote}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer — Cancel button for own pending leaves only. Approved /
          rejected leaves and HR events are read-only here; the user can't
          edit history or admin-managed events from this surface. */}
      {own && isLeave && status === "PENDING" && (
        <div className={s.eventFoot}>
          <button type="button" className={s.btnDanger}
                  onClick={onCancel} disabled={busy}>
            Cancel leave request
          </button>
        </div>
      )}
    </div>
  );
}

/** Build a 7×6 Mon→Sun grid covering the full month plus padding from
 *  neighbouring months. Padding cells are flagged `muted` so they
 *  render dimmed but stay clickable (so users can quickly file a leave
 *  on the first/last days of an adjacent month from the visible view). */
function buildGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = (first.getDay() + 6) % 7; // Mon=0...Sun=6
  const cells = [];
  const prevDays = new Date(year, month, 0).getDate();
  for (let i = startDow - 1; i >= 0; i--) {
    const d = prevDays - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    cells.push({ day: d, month: pm, year: py, muted: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, muted: false });
  }
  const nm = month === 11 ? 0 : month + 1;
  const ny = month === 11 ? year + 1 : year;
  let n = 1;
  while (cells.length < 42) {
    cells.push({ day: n++, month: nm, year: ny, muted: true });
  }
  return cells.map(c => ({
    ...c,
    iso: isoOf(new Date(c.year, c.month, c.day)),
  }));
}

const blankLeave = {
  title: "Vacation Leave",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  notes: "",
};

/** Pick the chip palette based on the event type and (for leaves) status.
 *  Keeps the visual taxonomy consistent with the school /calendar so the
 *  user can transfer the mental model. */
function eventStyle(ev, employeeCode) {
  const t = (ev.eventType || "").toLowerCase();
  if (t === "leave") {
    const own = ev.createdBy === employeeCode;
    const status = (ev.status || "PENDING").toUpperCase();
    if (status === "APPROVED") return own ? "leaveApproved" : "leaveOther";
    if (status === "REJECTED") return "leaveRejected";
    return "leavePending";
  }
  if (/holiday/.test(t)) return "holiday";
  if (/training/.test(t)) return "training";
  if (/meeting/.test(t)) return "meeting";
  if (/birthday/.test(t)) return "birthday";
  if (/exam/.test(t)) return "exam";
  if (/sports/.test(t)) return "sports";
  if (/class/.test(t)) return "klass";
  if (/field/.test(t)) return "field";
  return "otherEvent";
}

/** Human-readable event-type label. Falls back to the raw type string. */
function eventTypeLabel(ev) {
  const t = ev.eventType || "Event";
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

export default function MyCalendarPage() {
  const user = getUser() || {};
  const employeeCode = resolveEmployeeCode(user);

  const companyDefaults = useCompanyDefaults();

  const [employee, setEmployee] = useState(null);
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(blankLeave);
  const [pulse, setPulse]       = useState(0);

  // Cursor for the visible month — defaults to today's month.
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return { year: t.getFullYear(), month: t.getMonth() };
  });
  // Right-rail mode is driven by these two states. They're mutually
  // exclusive: setting one clears the other (see selectEvent / selectDay
  // below). When both are empty, the rail shows the default view
  // (leave balance + month-at-a-glance). This mirrors the school
  // /payroll-calendar's "Event Details" rail without admin controls.
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedDayIso,  setSelectedDayIso]  = useState("");
  // Direction of the most recent month change (for slide animation).
  // -1 = prev, 1 = next, 0 = none. Resets after the animation runs once.
  const [navDir, setNavDir] = useState(0);

  // Load data — employee profile + this year's events. We intentionally
  // fetch the *full* year so navigating months doesn't trigger refetches;
  // the dataset is small (a few hundred rows) and this keeps month nav
  // snappy without cache plumbing.
  useEffect(() => {
    if (!employeeCode) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const year = new Date().getFullYear();
    Promise.all([
      fetch(`${API_BASE}/api/employees/${employeeCode}`).then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE}/api/calendar/events?year=${year}&employeeCode=${employeeCode}`)
        .then(r => r.ok ? r.json() : []),
    ])
      .then(([emp, evs]) => {
        if (cancelled) return;
        setEmployee(emp);
        setEvents(Array.isArray(evs) ? evs : []);
      })
      .catch(() => { if (!cancelled) setError("Failed to load calendar data."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeCode, pulse]);

  // Live-update triggers — admin approvals happen out-of-band, so the
  // calendar's events list goes stale unless we refresh. Three light
  // signals keep the leave balance honest without coupling to the
  // notification system:
  //   • 45-second poll while the tab is visible (skipped when hidden so
  //     a backgrounded tab doesn't keep hammering the API)
  //   • "visibilitychange" → immediate refresh when the user returns
  //   • "approvals:changed" custom event so within-app actions in other
  //     pages can broadcast a hint to refetch
  useEffect(() => {
    if (!employeeCode) return;
    const bump = () => setPulse(p => p + 1);
    const onVisibility = () => {
      if (document.visibilityState === "visible") bump();
    };
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") bump();
    }, 45_000);
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("approvals:changed", bump);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("approvals:changed", bump);
    };
  }, [employeeCode]);

  // ── Leave-balance computation (unchanged from previous version) ─────
  const leaveBalances = useMemo(() => {
    const own = parseLeaveCredits(employee?.leavesJson);
    const credits = mergeWithDefaults(own, companyDefaults["work.defaultLeaves"]);
    const usedByName = {};
    for (const ev of events) {
      if ((ev.eventType || "").toLowerCase() !== "leave") continue;
      if (ev.createdBy !== employeeCode) continue;
      if (ev.status != null && ev.status !== "APPROVED") continue;
      const name = (ev.title || "Other Leave").trim();
      usedByName[name] = (usedByName[name] || 0) + rangeDays(ev.startDate, ev.endDate);
    }
    return credits.map(c => {
      const used = Object.entries(usedByName).find(
        ([k]) => k.toLowerCase() === c.name.toLowerCase()
      )?.[1] || 0;
      return { ...c, used, remaining: Math.max(0, c.days - used) };
    });
  }, [employee, events, employeeCode, companyDefaults]);

  // Visible events are filtered to those that intersect the user. We hide
  // OTHER people's leaves (they're not relevant to a personal calendar)
  // and keep everything else: holidays, training, meetings, birthdays,
  // plus the user's own leaves regardless of status.
  const visibleEvents = useMemo(() => events.filter(ev => {
    const t = (ev.eventType || "").toLowerCase();
    if (t !== "leave") return true;
    return ev.createdBy === employeeCode;
  }), [events, employeeCode]);

  // Bucket events by ISO date so day cells can look up O(1). For
  // multi-day events we add the same event under each weekday in the
  // [start, end] inclusive range — leaves auto-skip Sat/Sun via the
  // backend-provided dates.
  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of visibleEvents) {
      if (!ev.startDate) continue;
      const start = new Date(ev.startDate + "T00:00:00");
      const end = new Date((ev.endDate || ev.startDate) + "T00:00:00");
      if (isNaN(start) || isNaN(end)) continue;
      const cur = new Date(start);
      while (cur <= end) {
        const k = isoOf(cur);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(ev);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [visibleEvents]);

  const grid = useMemo(() => buildGrid(cursor.year, cursor.month), [cursor]);

  const monthLabel = `${MONTH_NAMES[cursor.month]} ${cursor.year}`;
  const today = todayIso();

  // Stats for the right-rail "this month" mini-counter.
  const monthStats = useMemo(() => {
    const inMonth = (iso) => iso.startsWith(`${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}`);
    let holidays = 0, training = 0, meetings = 0, myLeave = 0;
    for (const ev of visibleEvents) {
      if (!ev.startDate || !inMonth(ev.startDate)) continue;
      const t = (ev.eventType || "").toLowerCase();
      if (t === "leave" && ev.createdBy === employeeCode) myLeave++;
      else if (/holiday/.test(t)) holidays++;
      else if (/training/.test(t)) training++;
      else if (/meeting/.test(t)) meetings++;
    }
    return { holidays, training, meetings, myLeave };
  }, [visibleEvents, cursor, employeeCode]);

  const myLeaves = useMemo(() => visibleEvents
    .filter(ev => (ev.eventType || "").toLowerCase() === "leave" && ev.createdBy === employeeCode)
    .sort((a, b) => String(b.startDate).localeCompare(String(a.startDate))),
    [visibleEvents, employeeCode]);

  const upcoming = useMemo(() => {
    return visibleEvents
      .filter(ev => {
        const t = (ev.eventType || "").toLowerCase();
        if (t === "leave") return false; // shown elsewhere
        const end = new Date((ev.endDate || ev.startDate) + "T00:00:00");
        const todayDate = new Date(today + "T00:00:00");
        return end >= todayDate;
      })
      .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)))
      .slice(0, 6);
  }, [visibleEvents, today]);

  // Resolve which mode the right rail is in. `eventDetail` wins over
  // `dayList` so a chip-click always goes straight to detail view.
  const selectedEvent = useMemo(
    () => selectedEventId != null
      ? visibleEvents.find(ev => ev.id === selectedEventId) || null
      : null,
    [selectedEventId, visibleEvents]
  );
  const selectedDayEvents = selectedDayIso ? (eventsByDay.get(selectedDayIso) || []) : [];

  // Mode-toggle helpers — clearing the other state keeps the two
  // selections mutually exclusive without scattered setState calls.
  function selectEvent(id) { setSelectedEventId(id); setSelectedDayIso(""); }
  function selectDay(iso)  { setSelectedDayIso(iso); setSelectedEventId(null); }
  function clearSelection() { setSelectedEventId(null); setSelectedDayIso(""); }

  // If the currently-selected event disappears (e.g. the user cancelled
  // it and the refetch returned a smaller list), drop the orphaned
  // selection so the rail doesn't render against stale data.
  useEffect(() => {
    if (selectedEventId != null && !selectedEvent) {
      setSelectedEventId(null);
    }
  }, [selectedEventId, selectedEvent]);

  /** Open an event in the right-rail detail panel AND scroll the calendar
   *  to whichever month the event lives in. Used by the bottom-row lists
   *  so a row click highlights the corresponding chip in the grid above. */
  function openEvent(ev) {
    if (!ev) return;
    if (ev.startDate) {
      const d = new Date(ev.startDate + "T00:00:00");
      if (!isNaN(d) && (d.getFullYear() !== cursor.year || d.getMonth() !== cursor.month)) {
        const dir = (d.getFullYear() * 12 + d.getMonth())
                  - (cursor.year      * 12 + cursor.month);
        setNavDir(dir > 0 ? 1 : -1);
        setCursor({ year: d.getFullYear(), month: d.getMonth() });
      }
    }
    selectEvent(ev.id);
    // Smooth-scroll the page back to the calendar so the highlighted
    // chip is visible on viewports where the bottom row is below the fold.
    requestAnimationFrame(() => {
      gridRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  // ── Month nav ──────────────────────────────────────────────────────
  function gotoMonth(deltaMonths) {
    setNavDir(deltaMonths > 0 ? 1 : -1);
    setCursor(c => {
      const m = c.month + deltaMonths;
      const year = c.year + Math.floor(m / 12);
      const month = ((m % 12) + 12) % 12;
      return { year, month };
    });
    clearSelection();
  }
  function gotoToday() {
    const t = new Date();
    setNavDir(0);
    setCursor({ year: t.getFullYear(), month: t.getMonth() });
    selectDay(today);
  }

  // Reset slide-direction class so the same direction can animate again
  // on the next nav click. Using a key on the grid wrapper would re-run
  // the animation but force-remount the children, which we don't need —
  // a simple flag toggle is enough.
  const gridRef = useRef(null);
  useEffect(() => {
    if (!navDir || !gridRef.current) return;
    const el = gridRef.current;
    el.classList.remove(s.slideEnterPrev, s.slideEnterNext);
    void el.offsetWidth; // force reflow so the class re-add restarts the animation
    el.classList.add(navDir > 0 ? s.slideEnterNext : s.slideEnterPrev);
  }, [navDir, cursor]);

  // ── Submit a leave request ─────────────────────────────────────────
  function startNewLeave(prefill) {
    setForm({
      ...blankLeave,
      startDate: prefill?.start || "",
      endDate:   prefill?.end || "",
    });
    setShowForm(true);
    setError("");
  }

  /** When the user clears the start time, also clear the end time —
   *  end without start is meaningless, and leaving a stale end value
   *  trips up the time-range hint shown below the inputs. */
  function setFormStartTime(v) {
    setForm(p => ({ ...p, startTime: v, endTime: v ? p.endTime : "" }));
  }

  async function submitLeave(e) {
    e.preventDefault();
    if (!form.startDate) { setError("Pick a start date."); return; }
    setBusy(true); setError("");
    try {
      const body = {
        title: form.title.trim() || "Leave",
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        startTime: form.startTime || null,
        endTime:   form.endTime   || null,
        eventType: "Leave",
        affectsSalary: true,
        notes: form.notes.trim() || null,
        attendees: null,
        storeName: null,
      };
      const res = await fetch(`${API_BASE}/api/calendar/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Submit failed"));
      setShowForm(false);
      setForm(blankLeave);
      setPulse(p => p + 1);
      window.dispatchEvent(new CustomEvent("approvals:changed"));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function cancelMyLeave(ev) {
    if (!window.confirm(`Cancel "${ev.title}"?`)) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/calendar/events/${ev.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Cancel failed"));
      setPulse(p => p + 1);
      window.dispatchEvent(new CustomEvent("approvals:changed"));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (!employeeCode) {
    return (
      <div className={s.page}>
        <div className={s.emptyState}>
          <div className={s.emptyEmoji}>🔒</div>
          <h2>No employee record on file</h2>
          <p>Your account isn't linked to an employee profile yet. Please contact HR.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      {/* ── Page header ── */}
      <header className={s.pageHeader}>
        <div>
          <div className={s.eyebrow}>My Calendar</div>
          <h1 className={s.pageTitle}>Leaves &amp; HR events</h1>
          <p className={s.pageSub}>
            File a leave request and stay on top of holidays, training, and meetings.
          </p>
        </div>

        <button type="button" className={s.btnPrimary} onClick={() => startNewLeave()}>
          <span className={s.btnPlus}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          Request leave
        </button>
      </header>

      {error && <div className={s.error}>{error}</div>}

      {/* "Your leave was approved/edited by …" banner. Sits above the
          calendar so the user sees activity on their leaves before
          looking at the grid. */}
      <NotificationBanner entityTypes={["CALENDAR_EVENT"]} />

      {loading && <div className={s.tableEmpty}>Loading…</div>}

      {!loading && (
        <div className={s.layout}>
          {/* ═══════════════ Calendar card ═══════════════ */}
          <section className={s.calCard}>
            <div className={s.calToolbar}>
              <div className={s.toolbarLeft}>
                <button type="button" className={s.navBtn} onClick={() => gotoMonth(-1)}
                        aria-label="Previous month" title="Previous month">‹</button>
                <div className={s.monthLabel}>{monthLabel}</div>
                <button type="button" className={s.navBtn} onClick={() => gotoMonth(1)}
                        aria-label="Next month" title="Next month">›</button>
              </div>
              <button type="button" className={s.todayBtn} onClick={gotoToday}
                      title="Jump to today">Today</button>
            </div>

            <div className={s.dowRow}>
              {DOW.map(d => <div key={d} className={s.dowCell}>{d}</div>)}
            </div>

            <div className={s.grid} ref={gridRef}>
              {grid.map((cell, idx) => {
                const dayEvents = eventsByDay.get(cell.iso) || [];
                const isToday = cell.iso === today;
                const isSelected = cell.iso === selectedDayIso ||
                                   (selectedEvent && dayEvents.some(e => e.id === selectedEvent.id));
                const col = idx % 7;
                const isWeekend = col === 5 || col === 6; // Sat / Sun
                const visible = dayEvents.slice(0, 3);
                const overflow = dayEvents.length - visible.length;

                const cellClass = [
                  s.dayCell,
                  cell.muted ? s.dayMuted : "",
                  isToday && !cell.muted ? s.dayToday : "",
                  isSelected ? s.daySelected : "",
                  isWeekend ? s.dayWeekend : "",
                  dayEvents.length > 0 ? s.dayHasEvents : "",
                ].filter(Boolean).join(" ");

                return (
                  <div
                    key={cell.iso + idx}
                    role="button"
                    tabIndex={0}
                    className={cellClass}
                    onClick={() => selectDay(cell.iso)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectDay(cell.iso);
                      }
                    }}
                    style={{ animationDelay: `${idx * 8}ms` }}
                    aria-label={`${cell.iso}, ${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"}`}
                  >
                    <div className={s.dayHead}>
                      <span className={s.dayNum}>{cell.day}</span>
                      {isToday && !cell.muted && <span className={s.todayDot} aria-hidden="true" />}
                    </div>
                    {visible.length > 0 && (
                      <div className={s.chipStack}>
                        {visible.map((ev, ci) => {
                          const isActive = selectedEvent && ev.id === selectedEvent.id;
                          return (
                            <button
                              type="button"
                              key={ev.id + "-" + ci}
                              className={`${s.chip} ${s["chip_" + eventStyle(ev, employeeCode)]} ${isActive ? s.chipActive : ""}`}
                              title={ev.title}
                              onClick={(e) => { e.stopPropagation(); selectEvent(ev.id); }}
                            >
                              <span className={s.chipDot} aria-hidden="true" />
                              <span className={s.chipText}>{ev.title}</span>
                            </button>
                          );
                        })}
                        {overflow > 0 && (
                          <button type="button"
                                  className={s.chipMore}
                                  onClick={(e) => { e.stopPropagation(); selectDay(cell.iso); }}>
                            +{overflow} more
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className={s.legend}>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.legApproved}`} />My leave (approved)</span>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.legPending}`} />Pending</span>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.legHoliday}`} />Holiday</span>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.legTraining}`} />Training</span>
              <span className={s.legendItem}><span className={`${s.legendDot} ${s.legMeeting}`} />Meeting</span>
            </div>
          </section>

          {/* ═══════════════ Right rail (single panel, three modes) ═══════════════
              Modes:
                · "event"   — selectedEvent set: full event details + cancel
                · "day"     — selectedDayIso set: list of that day's events
                · "default" — neither set: leave balance + month-at-a-glance
              The wrapper carries a `key` so React remounts on mode change,
              which re-triggers the entrance animation for a smooth crossfade. */}
          <aside className={s.rail}>
            <div className={s.railPanel}
                 key={selectedEvent ? `e-${selectedEvent.id}` : selectedDayIso ? `d-${selectedDayIso}` : "default"}>
              {selectedEvent ? (
                /* ─── Mode: Event details ─── */
                <EventDetails ev={selectedEvent}
                              employeeCode={employeeCode}
                              busy={busy}
                              onClose={clearSelection}
                              onCancel={() => cancelMyLeave(selectedEvent)} />
              ) : selectedDayIso ? (
                /* ─── Mode: Day list ─── */
                <div className={s.dayCard}>
                  <div className={s.dayCardHead}>
                    <div>
                      <div className={s.dayCardEyebrow}>Selected day</div>
                      <div className={s.dayCardTitle}>{fmtDateLong(selectedDayIso)}</div>
                    </div>
                    <button type="button" className={s.dayCardClose}
                            onClick={clearSelection}
                            aria-label="Close day details">×</button>
                  </div>
                  {selectedDayEvents.length === 0 ? (
                    <div className={s.dayCardEmpty}>
                      <div className={s.dayCardEmptyText}>Nothing scheduled.</div>
                      <button type="button" className={s.btnGhostSm}
                              onClick={() => startNewLeave({ start: selectedDayIso, end: selectedDayIso })}>
                        + Request leave on this day
                      </button>
                    </div>
                  ) : (
                    <div className={s.dayCardList}>
                      {selectedDayEvents.map((ev, i) => {
                        const styleKey = eventStyle(ev, employeeCode);
                        return (
                          <button type="button"
                                  key={ev.id + "-" + i}
                                  className={s.dayItem}
                                  onClick={() => selectEvent(ev.id)}>
                            <span className={`${s.dayItemDot} ${s["dot_" + styleKey]}`} />
                            <div className={s.dayItemBody}>
                              <div className={s.dayItemTitle}>{ev.title}</div>
                              <div className={s.dayItemMeta}>
                                {eventTypeLabel(ev)}
                                {ev.startTime &&
                                  ` · ${ev.startTime}${ev.endTime ? ` → ${ev.endTime}` : ""}`}
                              </div>
                            </div>
                            <span className={s.dayItemArrow} aria-hidden="true">›</span>
                          </button>
                        );
                      })}
                      <button type="button" className={s.btnGhostSm}
                              onClick={() => startNewLeave({ start: selectedDayIso, end: selectedDayIso })}>
                        + Request leave on this day
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* ─── Mode: Default (balance + stats) ─── */
                <>
                  <div className={`${s.hero} ${s.heroMini}`}>
                    <div className={s.heroGlow} aria-hidden="true" />
                    <div className={s.heroBodyMini}>
                      <div className={s.heroEyebrow}>This year</div>
                      <div className={s.heroTitleMini}>Leave balance</div>
                      <div className={s.balanceList}>
                        {leaveBalances.length === 0 ? (
                          <div className={s.balanceEmpty}>No leave credits configured yet.</div>
                        ) : (
                          leaveBalances.map((b, i) => {
                            const pct = b.days > 0 ? Math.min(100, (b.used / b.days) * 100) : 0;
                            return (
                              <div key={b.name} className={s.balanceItem} style={{ animationDelay: `${i * 60}ms` }}>
                                <div className={s.balanceTop}>
                                  <span className={s.balanceName}>{b.name}</span>
                                  <span className={s.balanceCount}>
                                    <strong>{b.remaining}</strong>
                                    <span className={s.balanceTotal}>/ {b.days} left</span>
                                  </span>
                                </div>
                                <div className={s.balanceBarOuter}>
                                  <div className={s.balanceBar}
                                       style={{ width: `${pct}%`, animationDelay: `${i * 60 + 200}ms` }} />
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={s.statCard}>
                    <div className={s.statHead}>
                      <div className={s.statTitle}>{MONTH_NAMES[cursor.month]} at a glance</div>
                    </div>
                    <div className={s.statGrid}>
                      <div className={s.stat}>
                        <span className={`${s.statDot} ${s.legHoliday}`} />
                        <span className={s.statNum}>{monthStats.holidays}</span>
                        <span className={s.statLabel}>Holidays</span>
                      </div>
                      <div className={s.stat}>
                        <span className={`${s.statDot} ${s.legTraining}`} />
                        <span className={s.statNum}>{monthStats.training}</span>
                        <span className={s.statLabel}>Training</span>
                      </div>
                      <div className={s.stat}>
                        <span className={`${s.statDot} ${s.legMeeting}`} />
                        <span className={s.statNum}>{monthStats.meetings}</span>
                        <span className={s.statLabel}>Meetings</span>
                      </div>
                      <div className={s.stat}>
                        <span className={`${s.statDot} ${s.legApproved}`} />
                        <span className={s.statNum}>{monthStats.myLeave}</span>
                        <span className={s.statLabel}>My leaves</span>
                      </div>
                    </div>
                  </div>

                  <div className={s.railHint}>
                    <span className={s.railHintIcon} aria-hidden="true">›</span>
                    Tap an event to see details, or pick a day to add a leave.
                  </div>
                </>
              )}
            </div>
          </aside>

          {/* ═══════════════ Bottom row (full-width, two columns) ═══════════════
              These lists were previously stacked in the right rail, which made
              the page taller than the calendar grid and left a wide empty band
              underneath. Moving them here uses that horizontal space and keeps
              the right rail's height aligned with the calendar. */}
          <div className={s.bottomRow}>
            <div className={s.miniCard}>
              <div className={s.miniHead}>
                <div className={s.miniTitle}>My leave requests</div>
                <span className={s.miniSub}>{myLeaves.length} this year</span>
              </div>
              {myLeaves.length === 0 ? (
                <div className={s.miniEmpty}>You haven't filed any leaves yet.</div>
              ) : (
                <div className={s.miniList}>
                  {myLeaves.slice(0, 6).map((ev, i) => {
                    const status = (ev.status || "PENDING").toUpperCase();
                    const isActive = selectedEvent && ev.id === selectedEvent.id;
                    return (
                      <button type="button"
                              key={ev.id}
                              className={`${s.miniRow} ${isActive ? s.miniRowActive : ""}`}
                              style={{ animationDelay: `${i * 40}ms` }}
                              onClick={() => openEvent(ev)}>
                        <div className={s.miniRowMain}>
                          <div className={s.miniRowTitle}>{ev.title}</div>
                          <div className={s.miniRowMeta}>
                            {fmtDateLong(ev.startDate)}
                            {ev.endDate && ev.endDate !== ev.startDate &&
                              ` → ${fmtDateLong(ev.endDate)}`}
                            {ev.startTime &&
                              ` · ${ev.startTime}${ev.endTime ? ` → ${ev.endTime}` : ""}`}
                          </div>
                        </div>
                        <span className={
                          status === "APPROVED" ? s.pillOk :
                          status === "REJECTED" ? s.pillBad :
                          s.pillWait
                        }>{status}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={s.miniCard}>
              <div className={s.miniHead}>
                <div className={s.miniTitle}>Upcoming HR events</div>
                <span className={s.miniSub}>Next up</span>
              </div>
              {upcoming.length === 0 ? (
                <div className={s.miniEmpty}>No upcoming events.</div>
              ) : (
                <div className={s.miniList}>
                  {upcoming.map((ev, i) => {
                    const styleKey = eventStyle(ev, employeeCode);
                    const isActive = selectedEvent && ev.id === selectedEvent.id;
                    return (
                      <button type="button"
                              key={ev.id}
                              className={`${s.miniRow} ${isActive ? s.miniRowActive : ""}`}
                              style={{ animationDelay: `${i * 40}ms` }}
                              onClick={() => openEvent(ev)}>
                        <div className={s.miniDate}>
                          <div className={s.miniDay}>
                            {new Date(ev.startDate + "T00:00:00").getDate()}
                          </div>
                          <div className={s.miniMonth}>
                            {new Date(ev.startDate + "T00:00:00")
                              .toLocaleString(undefined, { month: "short" })}
                          </div>
                        </div>
                        <div className={s.miniRowMain}>
                          <div className={s.miniRowTitle}>{ev.title}</div>
                          <div className={s.miniRowMeta}>{eventTypeLabel(ev)}</div>
                        </div>
                        <span className={`${s.miniDot} ${s["dot_" + styleKey]}`} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Leave request modal (unchanged submit flow) ── */}
      {showForm && createPortal(
        /* Click-outside close: only fire when the click target is the overlay
           itself (not a child like the form, an input, or a select option).
           Using e.target===e.currentTarget is more reliable than a class
           probe — the latter mis-triggered after CSS-module hashing changed. */
        <div className={s.overlay}
             onMouseDown={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <form className={s.modal} onSubmit={submitLeave}
                onMouseDown={(e) => e.stopPropagation()}>
            <div className={s.modalHead}>
              <div>
                <div className={s.modalEyebrow}>Leave request</div>
                <div className={s.modalTitle}>File a new leave</div>
              </div>
              <button type="button" className={s.modalClose}
                      onClick={() => setShowForm(false)}>×</button>
            </div>

            <div className={s.modalBody}>
              <div className={s.field}>
                <label>Leave type</label>
                <select className={s.input} value={form.title}
                        onChange={e => setForm(p => ({ ...p, title: e.target.value }))}>
                  {leaveBalances.length === 0 && <option>Vacation Leave</option>}
                  {leaveBalances.map(b => (
                    <option key={b.name} value={b.name}>
                      {b.name} ({b.remaining} day{b.remaining === 1 ? "" : "s"} left)
                    </option>
                  ))}
                  <option value="Other Leave">Other Leave</option>
                </select>
              </div>

              <div className={s.fieldRow}>
                <div className={s.field}>
                  <label>From</label>
                  <input type="date" className={s.input} value={form.startDate}
                         onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
                         required />
                </div>
                <div className={s.field}>
                  <label>To <span className={s.muted}>(optional)</span></label>
                  <input type="date" className={s.input} value={form.endDate}
                         min={form.startDate}
                         onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} />
                </div>
              </div>

              {/* Time row — leave blank for a full-day leave. Filling it in
                  signals a half-day / partial-day leave so payroll can prorate. */}
              <div className={s.fieldRow}>
                <div className={s.field}>
                  <label>Start time <span className={s.muted}>(optional)</span></label>
                  <TimePicker value={form.startTime} onChange={setFormStartTime} />
                </div>
                <div className={s.field}>
                  <label>End time <span className={s.muted}>(optional)</span></label>
                  <TimePicker value={form.endTime}
                              onChange={v => setForm(p => ({ ...p, endTime: v }))}
                              disabled={!form.startTime} />
                </div>
              </div>

              <div className={s.field}>
                <label>Reason <span className={s.muted}>(optional)</span></label>
                <textarea className={s.input} rows={3}
                          placeholder="Anything HR should know"
                          value={form.notes}
                          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>

              {form.startDate && (
                <div className={s.modalNote}>
                  Total weekdays: <strong>
                    {rangeDays(form.startDate, form.endDate || form.startDate)}
                  </strong>
                  {form.startTime && (
                    <>
                      {" · Time: "}
                      <strong>
                        {form.startTime}
                        {form.endTime ? ` → ${form.endTime}` : ""}
                      </strong>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className={s.modalFoot}>
              <button type="button" className={s.btnGhost}
                      onClick={() => setShowForm(false)} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className={s.btnPrimary} disabled={busy}>
                {busy ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}
